import dayjs from 'dayjs'

/**
 * CC-50162 — Matter Metrics' five-year window cap.
 *
 * CB rejects an over-long range inside the stored proc that builds the budget
 * `#Periods` table:
 *
 *   IF (@maxDate > DATEADD(YEAR, 5, @minDate) OR @minDate > '9995-01-01')
 *       RAISERROR('The given date range must be at most 5 years in length', 17, -1)
 *
 * That is a deliberate guard against period-table explosion, not a malfunction,
 * so validating in the client is a COMPLETE fix rather than a mask over a
 * server bug — the request it prevents could never have succeeded.
 *
 * QA filed this as "API 500 for early date ranges (≤2020)". The start date is
 * irrelevant: 2020-01-01 → 2020-12-31 returns 200 with 530 rows on vladdemo,
 * while 2021-06-01 → today (5y2m) returns 500. Leaving the end date at today is
 * what made early start dates look cursed.
 */
export const MAX_RANGE_YEARS = 5

/**
 * True when CB would reject this window. Mirrors the proc's comparison exactly:
 * allowed iff `end <= start + 5 years`, so an exactly-five-year span passes.
 *
 * Returns false for absent or unparseable dates — those are somebody else's
 * validation, and reporting them as "too long" would be a misleading message.
 */
export const rangeTooLong = (start, end) => {
  if (!start || !end) return false
  const from = dayjs(start)
  const to = dayjs(end)
  if (!from.isValid() || !to.isValid()) return false
  return to.isAfter(from.add(MAX_RANGE_YEARS, 'year'))
}

export const rangeTooLongMessage = () =>
  `The date range must be at most ${MAX_RANGE_YEARS} years. Narrow the start or end date and try again.`
